import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Bell, Pause, Play, RotateCcw, Settings2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

type TimerMode = "work" | "shortBreak" | "longBreak";

interface TimerSettings {
  work: number;
  shortBreak: number;
  longBreak: number;
}

export default function PomodoroTimer() {
  // Timer settings in minutes
  const [settings, setSettings] = useState<TimerSettings>({
    work: 25,
    shortBreak: 5,
    longBreak: 15,
  });

  const [mode, setMode] = useState<TimerMode>("work");
  const [timeLeft, setTimeLeft] = useState(settings[mode] * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [completedSessions, setCompletedSessions] = useState(0);
  const [isAudioInitialized, setIsAudioInitialized] = useState(false);
  
  // Refs for timer management
  const workerRef = useRef<Worker | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const soundBufferRef = useRef<AudioBuffer | null>(null);
  const fallbackTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [workerSupported, setWorkerSupported] = useState(true);

  // Update document title with timer
  useEffect(() => {
    const formattedTime = formatTime(timeLeft);
    const modeDisplay = mode === "work" ? "Work" : mode === "shortBreak" ? "Break" : "Long Break";
    document.title = `${formattedTime} - ${modeDisplay} - Pomodoro`;

    // Cleanup - reset title when component unmounts
    return () => {
      document.title = "Pomodoro Timer - Habitualy";
    };
  }, [timeLeft, mode]);

  // Initialize Web Worker with fallback
  useEffect(() => {
    try {
      if (typeof Worker !== 'undefined') {
        workerRef.current = new Worker('/timer-worker.js');
        workerRef.current.onmessage = (e) => {
          const { type, remaining } = e.data;
          if (type === 'TICK') {
            setTimeLeft(remaining);
          } else if (type === 'COMPLETE') {
            handleTimerComplete();
          }
        };
        workerRef.current.onerror = (error) => {
          console.warn('Worker error, falling back to setTimeout:', error);
          setWorkerSupported(false);
        };
      } else {
        console.warn('Web Workers not supported, using fallback timer');
        setWorkerSupported(false);
      }
    } catch (error) {
      console.warn('Failed to create worker, using fallback timer:', error);
      setWorkerSupported(false);
    }

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const initializeAudio = async () => {
    if (!isAudioInitialized && !audioContextRef.current) {
      try {
        // Create AudioContext with mobile-friendly approach
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) {
          console.warn('AudioContext not supported on this browser');
          return;
        }

        const audioContext = new AudioContextClass();
        audioContextRef.current = audioContext;

        // For mobile browsers, ensure AudioContext is resumed
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        // Fetch and decode audio file with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

        const response = await fetch('/notification.mp3', { 
          signal: controller.signal 
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Failed to fetch audio: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        soundBufferRef.current = audioBuffer;
        setIsAudioInitialized(true);
        console.log('Audio initialized successfully');
      } catch (error) {
        console.warn('Audio initialization failed:', error);
        // Don't throw - let timer work without audio
        setIsAudioInitialized(false);
      }
    }
  };

  const playNotificationSound = async () => {
    try {
      if (!isAudioInitialized) {
        await initializeAudio();
      }

      if (audioContextRef.current && soundBufferRef.current) {
        // Resume audio context if it's suspended (common on mobile)
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }

        // Create and connect nodes
        const source = audioContextRef.current.createBufferSource();
        source.buffer = soundBufferRef.current;
        source.connect(audioContextRef.current.destination);
        
        // Play sound
        source.start(0);
      }
    } catch (error) {
      console.warn('Error playing sound:', error);
      // Don't throw - sound failure shouldn't break the timer
    }
  };

  // Timer logic with fallback
  useEffect(() => {
    if (isRunning) {
      if (workerSupported && workerRef.current) {
        // Use Web Worker
        workerRef.current.postMessage({
          type: 'START',
          duration: timeLeft,
          timestamp: Date.now()
        });
      } else {
        // Use fallback setTimeout timer
        const startFallbackTimer = () => {
          if (fallbackTimerRef.current) {
            clearTimeout(fallbackTimerRef.current);
          }
          
          if (timeLeft > 0) {
            fallbackTimerRef.current = setTimeout(() => {
              setTimeLeft(prev => {
                const newTime = prev - 1;
                if (newTime <= 0) {
                  handleTimerComplete();
                  return 0;
                }
                return newTime;
              });
            }, 1000);
          }
        };
        startFallbackTimer();
      }
    } else {
      // Stop timer
      if (workerRef.current) {
        workerRef.current.postMessage({ type: 'STOP' });
      }
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
      }
    }

    return () => {
      if (workerRef.current) {
        workerRef.current.postMessage({ type: 'STOP' });
      }
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
      }
    };
  }, [isRunning, timeLeft, workerSupported]);

  // Update timeLeft when settings or mode changes
  useEffect(() => {
    const newTime = settings[mode] * 60;
    setTimeLeft(newTime);
    if (isRunning) {
      if (workerSupported && workerRef.current) {
        workerRef.current.postMessage({
          type: 'START',
          duration: newTime,
          timestamp: Date.now()
        });
      }
      // For fallback timer, the effect above will handle the restart
    }
  }, [settings, mode]);

  const handleTimerComplete = async () => {
    setIsRunning(false);
    
    // Play notification sound
    await playNotificationSound();

    // Show notification
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("Timer Complete!", {
        body: `${mode.charAt(0).toUpperCase() + mode.slice(1)} session completed!`,
        icon: "/logo.png",
        silent: true, // Use our custom sound instead
      });
    }

    // Toast notification
    toast("Timer Complete!", {
      description: `${mode.charAt(0).toUpperCase() + mode.slice(1)} session completed!`,
    });

    // Update sessions and switch modes
    if (mode === "work") {
      setCompletedSessions(prev => {
        const newCount = prev + 1;
        if (newCount % 4 === 0) {
          setMode("longBreak");
        } else {
          setMode("shortBreak");
        }
        return newCount;
      });
    } else {
      setMode("work");
    }
  };

  const toggleTimer = async () => {
    try {
      if (!isRunning) {
        // Try to initialize audio but don't let it block the timer
        try {
          if (!isAudioInitialized) {
            await initializeAudio();
          }
          
          // Request notification permissions (non-blocking)
          if ("Notification" in window && Notification.permission === "default") {
            await Notification.requestPermission();
          }
        } catch (audioError) {
          console.warn('Audio/notification setup failed:', audioError);
          // Continue with timer even if audio fails
        }
      }
      setIsRunning(!isRunning);
    } catch (error) {
      console.error('Error starting timer:', error);
      toast.error("Failed to start timer. Please try again.");
    }
  };

  const resetTimer = () => {
    // Stop all timers
    if (workerRef.current) {
      workerRef.current.postMessage({ type: 'STOP' });
    }
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    setIsRunning(false);
    setTimeLeft(settings[mode] * 60);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const progress = (timeLeft / (settings[mode] * 60)) * 100;

  return (
    <Card className="p-6 relative overflow-hidden">
      {/* Progress bar */}
      <div 
        className="absolute top-0 left-0 h-1 bg-primary transition-all duration-1000"
        style={{ width: `${progress}%` }}
      />

      {/* Mode selector */}
      <div className="flex justify-center gap-2 mb-8">
        <Button
          variant={mode === "work" ? "default" : "outline"}
          onClick={() => setMode("work")}
          className="w-28"
        >
          Work
        </Button>
        <Button
          variant={mode === "shortBreak" ? "default" : "outline"}
          onClick={() => setMode("shortBreak")}
          className="w-28"
        >
          Short Break
        </Button>
        <Button
          variant={mode === "longBreak" ? "default" : "outline"}
          onClick={() => setMode("longBreak")}
          className="w-28"
        >
          Long Break
        </Button>
      </div>

      {/* Timer display */}
      <div className="text-center mb-8">
        <h2 className="text-7xl font-bold font-mono tracking-wider">
          {formatTime(timeLeft)}
        </h2>
        <p className="text-muted-foreground mt-2">
          Session {completedSessions + 1} - {mode === "work" ? "Focus Time" : "Break Time"}
        </p>
      </div>

      {/* Controls */}
      <div className="flex justify-center items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={resetTimer}
          disabled={timeLeft === settings[mode] * 60}
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button size="lg" onClick={toggleTimer} className="w-32">
          {isRunning ? (
            <Pause className="h-4 w-4 mr-2" />
          ) : (
            <Play className="h-4 w-4 mr-2" />
          )}
          {isRunning ? "Pause" : "Start"}
        </Button>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="icon">
              <Settings2 className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Timer Settings</DialogTitle>
              <DialogDescription>
                Customize your work and break durations. Changes will take effect on the next session.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Work Duration: {settings.work} minutes</Label>
                </div>
                <Slider
                  value={[settings.work]}
                  min={1}
                  max={60}
                  step={1}
                  onValueChange={([value]) =>
                    setSettings((prev) => ({ ...prev, work: value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Short Break: {settings.shortBreak} minutes</Label>
                </div>
                <Slider
                  value={[settings.shortBreak]}
                  min={1}
                  max={30}
                  step={1}
                  onValueChange={([value]) =>
                    setSettings((prev) => ({ ...prev, shortBreak: value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Long Break: {settings.longBreak} minutes</Label>
                </div>
                <Slider
                  value={[settings.longBreak]}
                  min={5}
                  max={45}
                  step={1}
                  onValueChange={([value]) =>
                    setSettings((prev) => ({ ...prev, longBreak: value }))
                  }
                />
              </div>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Bell className="h-4 w-4" />
                <span>Sound and desktop notifications enabled</span>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Card>
  );
} 